import { Link, useNavigate } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { buildResourceTypeUri } from "@/hooks/useRemaUriBuilder"
import { ArrowLeft, Loader2, Edit, Settings2, Link2, GitBranch, KeyRound, Database } from "lucide-react"
import { ProfileHeader, ProfileSection } from "@/components/sensnet/ProfileLayout"

interface ResourceTypeDetailPageProps {
  resourceTypeNumber: number | string
}

export function ResourceTypeDetailPage({ resourceTypeNumber }: ResourceTypeDetailPageProps) {
  const navigate = useNavigate()

  const { data, isLoading, isError } = useApiQuery("getResourceTypes", {})
  const { data: instancesData } = useApiQuery("getResourceInstances", {})

  const resourceTypes = (data as any)?.content?.resourceTypes || (data as any)?.resourceTypes || []
  const rt = resourceTypes.find(
    (r: any) => String(r.resourceTypeNumber) === String(resourceTypeNumber)
  )

  const instanceCount = ((instancesData as any)?.content?.resourceInstances || (instancesData as any)?.resourceInstances || []).filter(
    (i: any) => String(i.resourceTypeNumber) === String(resourceTypeNumber)
  ).length

  const parentNames = (rt?.parentResourceTypes || []).map((id: number) => {
    const found = resourceTypes.find((r: any) => r.resourceTypeNumber === id)
    return found ? { number: id, name: found.resourceTypeName } : { number: id, name: `Type #${id}` }
  })

  const uri = rt ? buildResourceTypeUri({
    namespace: rt.namespace ?? 'cnx',
    tenantId: rt.namespace === 'frg' ? 897 : 997,
    category: rt.resourceTypeCategory ?? '',
    parentNames: parentNames.map((p: any) => p.name),
    resourceTypeName: rt.resourceTypeName ?? '',
  }) : ''

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="text-muted-foreground">Loading resource type details...</span>
        </div>
      </div>
    )
  }

  if (isError || !rt) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-sm text-muted-foreground">Resource type not found.</p>
        <Button variant="outline" onClick={() => navigate({ to: '/rema/resource-types' })}>Back to list</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button asChild variant="ghost" size="sm" className="gap-2 -ml-2">
          <Link to="/rema/resource-types">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </Button>
        <Button asChild size="sm" className="gap-2">
          <Link to="/rema/resource-types/$resourceTypeNumber/edit" params={{ resourceTypeNumber: String(rt.resourceTypeNumber) }}>
            <Edit className="h-4 w-4" />
            Edit
          </Link>
        </Button>
      </div>

      <ProfileHeader
        title={rt.resourceTypeName}
        subtitle={`Resource Type Number: ${rt.resourceTypeNumber}`}
        subtitleIcon={<Settings2 className="h-4 w-4" />}
        badge={rt.resourceTypeCategory}
      />

      <ProfileSection title="Identity" icon={<Settings2 className="h-4 w-4" />} variant="dark">
        <div className="space-y-3 p-5">
          <div>
            <p className="text-xs text-muted-foreground">Name</p>
            <p className="text-sm">{rt.resourceTypeName}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Category</p>
            <p className="text-sm">{rt.resourceTypeCategory}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Namespace</p>
            <p className="text-sm font-mono">{rt.namespace}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Description</p>
            <p className="text-sm">{rt.resourceTypeDescription || '—'}</p>
          </div>
        </div>
      </ProfileSection>

      <ProfileSection title="Resource URI" icon={<Link2 className="h-4 w-4" />} variant="dark">
        <div className="space-y-3 p-5">
          <div className="rounded-md border bg-muted/50 px-3 py-2">
            <code className="text-sm font-mono break-all">{uri || '—'}</code>
          </div>
        </div>
      </ProfileSection>

      <ProfileSection title="Parent Chain" icon={<GitBranch className="h-4 w-4" />} variant="dark">
        <div className="space-y-3 p-5">
          {parentNames.length > 0 ? (
            <ol className="space-y-1">
              {parentNames.map((p: any, i: number) => (
                <li key={p.number} className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">{i + 1}.</span>
                  <Link
                    to="/rema/resource-types/$resourceTypeNumber"
                    params={{ resourceTypeNumber: String(p.number) }}
                    className="text-primary hover:underline"
                  >
                    {p.name}
                  </Link>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-sm text-muted-foreground">No parent types</p>
          )}
        </div>
      </ProfileSection>

      <ProfileSection title="Permissions" icon={<KeyRound className="h-4 w-4" />} variant="dark">
        <div className="space-y-3 p-5">
          {rt.permissions?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {rt.permissions.map((p: string) => (
                <Badge key={p} variant="secondary">{p}</Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No permissions defined</p>
          )}
        </div>
      </ProfileSection>

      <ProfileSection title="Associated Instances" icon={<Database className="h-4 w-4" />} variant="dark">
        <div className="space-y-3 p-5">
          <p className="text-2xl font-bold">{instanceCount}</p>
          <p className="text-sm text-muted-foreground">resource instance{instanceCount !== 1 ? 's' : ''} of this type</p>
        </div>
      </ProfileSection>
    </div>
  )
}
